import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  SupportTicket,
  SupportTicketCreate,
  SupportTicketUpdate,
  SupportTicketListQuery,
  SupportTicketListResponse,
  SupportTicketStatusCounts,
  SupportTicketThread,
  SupportTicketAttachment
} from '../models/support-ticket.model';

/**
 * Support Ticket Service
 * Handles support ticket CRUD operations
 */
@Injectable({
  providedIn: 'root'
})
export class SupportTicketService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Create a new support ticket
   */
  async createTicket(data: SupportTicketCreate): Promise<SupportTicket> {
    const response = await firstValueFrom(
      this.http.post<{ ticket: SupportTicket }>(
        `${this.apiUrl}/support-tickets`,
        data
      )
    );
    return response.ticket;
  }

  /**
   * Get tickets for an organization with optional filtering
   */
  async getOrganizationTickets(
    organizationId: string,
    query?: SupportTicketListQuery
  ): Promise<SupportTicketListResponse> {
    let params = new HttpParams();

    if (query?.status) {
      params = params.set('status', query.status);
    }
    if (query?.priority) {
      params = params.set('priority', query.priority);
    }
    if (query?.category) {
      params = params.set('category', query.category);
    }
    if (query?.droneId) {
      params = params.set('droneId', query.droneId);
    }
    if (query?.search) {
      params = params.set('search', query.search);
    }
    if (query?.page) {
      params = params.set('page', query.page.toString());
    }
    if (query?.limit) {
      params = params.set('limit', query.limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<SupportTicketListResponse>(
        `${this.apiUrl}/support-tickets/organization/${organizationId}`,
        { params }
      )
    );
    return response;
  }

  /**
   * Get ticket status counts for an organization
   */
  async getOrganizationTicketCounts(organizationId: string): Promise<SupportTicketStatusCounts> {
    const response = await firstValueFrom(
      this.http.get<{ counts: SupportTicketStatusCounts }>(
        `${this.apiUrl}/support-tickets/organization/${organizationId}/counts`
      )
    );
    return response.counts;
  }

  /**
   * Get a specific ticket by ID
   */
  async getTicket(id: string): Promise<SupportTicket | null> {
    const response = await firstValueFrom(
      this.http.get<{ ticket: SupportTicket }>(
        `${this.apiUrl}/support-tickets/${id}`
      )
    );
    return response.ticket || null;
  }

  /**
   * Update ticket status (resolve, close, reopen)
   */
  async updateTicketStatus(id: string, status: 'open' | 'resolved' | 'closed'): Promise<SupportTicket> {
    const response = await firstValueFrom(
      this.http.patch<{ ticket: SupportTicket }>(
        `${this.apiUrl}/support-tickets/${id}/status`,
        { status }
      )
    );
    return response.ticket;
  }

  /**
   * Withdraw (close) a support ticket with a reason
   *
   * @param id - Ticket ID
   * @param reason - Withdrawal reason: 'created_by_mistake' | 'resolved_on_my_own' | 'duplicate_ticket' | 'no_longer_needed' | 'other'
   * @param details - Optional additional details (used when reason is 'other')
   */
  async deleteTicket(id: string, reason: string = 'no_longer_needed', details?: string): Promise<void> {
    await firstValueFrom(
      this.http.request('DELETE', `${this.apiUrl}/support-tickets/${id}`, {
        body: { reason, details }
      })
    );
  }

  /**
   * Get tickets for a specific drone
   */
  async getDroneTickets(droneId: string): Promise<SupportTicket[]> {
    const response = await firstValueFrom(
      this.http.get<{ tickets: SupportTicket[] }>(
        `${this.apiUrl}/support-tickets/drone/${droneId}`
      )
    );
    return response.tickets || [];
  }

  /**
   * Get threads (conversation history) for a ticket
   */
  async getTicketThreads(ticketId: string): Promise<SupportTicketThread[]> {
    const response = await firstValueFrom(
      this.http.get<{ threads: SupportTicketThread[] }>(
        `${this.apiUrl}/support-tickets/${ticketId}/threads`
      )
    );
    return response.threads || [];
  }

  /**
   * Add a reply/thread to a ticket
   */
  async addTicketThread(ticketId: string, content: string): Promise<SupportTicketThread> {
    const response = await firstValueFrom(
      this.http.post<{ thread: SupportTicketThread }>(
        `${this.apiUrl}/support-tickets/${ticketId}/threads`,
        { content, isPublic: true, threadType: 'reply' }
      )
    );
    return response.thread;
  }

  /**
   * Get attachments for a ticket
   */
  async getTicketAttachments(ticketId: string): Promise<SupportTicketAttachment[]> {
    const response = await firstValueFrom(
      this.http.get<{ attachments: SupportTicketAttachment[] }>(
        `${this.apiUrl}/support-tickets/${ticketId}/attachments`
      )
    );
    return response.attachments || [];
  }

  /**
   * Upload attachments to a ticket
   * Used after ticket creation to upload any attached files
   *
   * @param ticketId - Ticket ID
   * @param files - Array of files to upload
   * @returns Upload result with array of uploaded attachments
   */
  async uploadAttachments(ticketId: string, files: File[]): Promise<{
    attachments: SupportTicketAttachment[];
    uploaded: number;
    errors?: string[];
  }> {
    const formData = new FormData();

    // Append each file with the same key 'file' (backend expects array)
    for (const file of files) {
      formData.append('file', file, file.name);
    }

    const response = await firstValueFrom(
      this.http.post<{
        attachments: SupportTicketAttachment[];
        uploaded: number;
        errors?: string[];
      }>(
        `${this.apiUrl}/support-tickets/${ticketId}/attachments`,
        formData
      )
    );

    return response;
  }
}
